import { AnchorHTMLAttributes, ButtonHTMLAttributes, MouseEvent, ReactNode, Ref, forwardRef, type JSX } from 'react';

import reactToText from 'react-to-text';

import * as s from './NavLink.css';
import { getClassNames } from '../../util/css';
import { RecipeVariants } from '../../vanilla-extract.css';

type NavLinkRecipeVariants = RecipeVariants<typeof s.navLinkRecipe>;

const sparkyClassName = 'sparky-navlink';

export const NavLink = forwardRef<HTMLAnchorElement | HTMLButtonElement, NavLinkProps>(
  (
    {
      children,
      variant = 'primary',
      isCurrent = false,
      href,
      target,
      type = 'button',
      leftIcon,
      rightIcon,
      onClick,
      tone = 'onLight',
      className = '',
      justifyContent = 'initial',
    },
    ref,
  ) => {
    const linkClass = getClassNames(
      sparkyClassName,
      s.navLinkRecipe({ variant, isCurrent, tone, justifyContent }),
      tone === 'onLight' ? s.focusOnLight : s.focusOnColor,
      className,
    );

    const containerClass = getClassNames(
      s.container,
      leftIcon && s.containerWithLeftIcon,
      rightIcon && s.containerWithRightIcon,
    );

    const textClass = getClassNames(
      s.textBase,
      s.textForVariant[variant as keyof typeof s.textForVariant],
      variant === 'primary' && s.textInsidePrimary,
      (variant === 'secondary' || variant === 'tertiary') && tone === 'onColor' && s.textInsideSecondaryOnColor,
      (variant === 'secondary' || variant === 'tertiary') && isCurrent && s.textUnderlineOnCurrentSecondary,
    );

    const linkContent = (
      <>
        {leftIcon}
        <span className={containerClass}>
          <span className={textClass}>{children}</span>
        </span>
        {rightIcon}
      </>
    );

    const commonProps = {
      'aria-current': isCurrent ? ('page' as const) : undefined,
      className: linkClass,
      'data-label': reactToText(children),
      onClick,
    };

    return href ? (
      <a
        {...commonProps}
        href={href}
        target={target || undefined}
        rel={target === '_blank' ? 'noreferrer' : undefined}
        ref={ref as Ref<HTMLAnchorElement>}>
        {linkContent}
      </a>
    ) : (
      <button {...commonProps} type={type} ref={ref as Ref<HTMLButtonElement>}>
        {linkContent}
      </button>
    );
  },
);

NavLink.displayName = 'NavLink';
NavLink.toString = () => `.${sparkyClassName}`;

type NavLinkButtonProps = {
  href?: never;
  target?: never;
  type?: ButtonHTMLAttributes<HTMLButtonElement>['type'];
};

type NavLinkAnchorProps = {
  href?: string;
  target?: AnchorHTMLAttributes<HTMLAnchorElement>['target'];
  type?: never;
};

type NavLinkProps = NavLinkRecipeVariants & {
  onClick?: (event: MouseEvent<HTMLAnchorElement | HTMLButtonElement>) => void;
  /** Text shown inside the link. */
  children: ReactNode;
  /** Icon shown to the left of the text. */
  leftIcon?: JSX.Element;
  /** Icon shown to the right of the text. */
  rightIcon?: JSX.Element;
  className?: string;
} & (NavLinkAnchorProps | NavLinkButtonProps);
